/**
 * Settings → Reports (owners and admins; Report & block, contracts moderation.ts): the open message reports, newest
 * first, each with its author, conversation, preview, reason, note, reporter and time. "Delete message" deletes it
 * (the existing deleteMessage) and resolves the report with `message_deleted`; "Dismiss" resolves it with `none`. A
 * resolved report leaves the list. Online only.
 */
import type { MessageReport, ReportAction } from "@bundle/contracts";
import type { MessageDescriptor } from "../i18n.ts";
import { msg } from "../i18n.ts";
import { reportReasonLabel } from "../logic/moderation.ts";
import { memberName } from "../logic/workspace.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type ReportRow = {
  id: string;
  report: MessageReport;
  authorName: string;
  authorAvatarUrl?: string;
  reporterName: string;
  /** "#design", or "a direct message" (descriptor) for DMs and conversations I can't see. */
  conversation: MessageDescriptor | string;
  reason: MessageDescriptor;
  /** Being resolved (its buttons are disabled). */
  busy: boolean;
};

export type ReportsState = {
  status: "loading" | "ready" | "error";
  rows: ReportRow[];
  /** Owners and admins only: others see nothing (and the nav hides the section). */
  allowed: boolean;
  /** The last resolve: done (deleted / dismissed) or failed; absent = nothing to say. */
  notice?: "deleted" | "dismissed" | "failed";
};

/** Owners and admins review reports (the API answers 403 to everyone else). */
export function canReviewReports(role: string | undefined): boolean {
  return role === "owner" || role === "admin";
}

export class ReportsViewModel extends ViewModel<ReportsState> {
  private reports: MessageReport[] = [];
  private busy = new Set<string>();

  constructor(private readonly ws: WorkspaceViewModel) {
    const me = ws.state.cache?.members[ws.state.meId];
    super({ status: "loading", rows: [], allowed: canReviewReports(me?.role) });
    this.onDispose(ws.subscribe(() => this.rederive()));
  }

  load(): void {
    if (!this.state.allowed) return;
    this.update({ status: this.reports.length === 0 ? "loading" : "ready" });
    this.ws.deps.api.listReports(this.ws.wsId, "open").then(
      (reports) => {
        if (this.isDisposed) return;
        this.reports = reports;
        this.update({ status: "ready", rows: this.rows() });
      },
      () => {
        if (!this.isDisposed) this.update({ status: "error" });
      },
    );
  }

  /** Deletes the reported message, then resolves the report (`message_deleted`). */
  deleteMessage(reportId: string): void {
    const r = this.reports.find((x) => x.id === reportId);
    if (r === undefined) return;
    void this.resolve(r, "message_deleted", () => this.ws.deps.api.deleteMessage(this.ws.wsId, r.messageId));
  }

  /** Resolves the report without acting on the message (`none`). */
  dismiss(reportId: string): void {
    const r = this.reports.find((x) => x.id === reportId);
    if (r === undefined) return;
    void this.resolve(r, "none");
  }

  clearNotice(): void {
    if (this.state.notice !== undefined) this.update({ notice: undefined });
  }

  private async resolve(r: MessageReport, action: ReportAction, before?: () => Promise<void>): Promise<void> {
    if (this.busy.has(r.id)) return;
    this.busy.add(r.id);
    this.update({ rows: this.rows() });
    try {
      if (before !== undefined) await before();
      await this.ws.deps.api.resolveReport(this.ws.wsId, r.id, { status: "resolved", action });
      if (this.isDisposed) return;
      this.reports = this.reports.filter((x) => x.id !== r.id);
      this.busy.delete(r.id);
      this.update({ rows: this.rows(), notice: action === "message_deleted" ? "deleted" : "dismissed" });
    } catch {
      if (this.isDisposed) return;
      this.busy.delete(r.id);
      this.update({ rows: this.rows(), notice: "failed" });
    }
  }

  private rederive(): void {
    const me = this.ws.state.cache?.members[this.ws.state.meId];
    const allowed = canReviewReports(me?.role);
    if (allowed !== this.state.allowed) this.update({ allowed });
    if (this.reports.length > 0) this.update({ rows: this.rows() });
  }

  private rows(): ReportRow[] {
    const cache = this.ws.state.cache;
    return this.reports.map((r) => {
      const c = cache?.channels[r.channelId];
      const author = cache?.members[r.authorId];
      const row: ReportRow = {
        id: r.id,
        report: r,
        authorName: cache === undefined ? r.authorId : memberName(cache, r.authorId),
        reporterName: cache === undefined ? r.reporterId : memberName(cache, r.reporterId),
        conversation: c === undefined || c.kind === "dm" || c.kind === "group_dm" ? msg("moderation.reports.directMessage") : `#${c.name}`,
        reason: reportReasonLabel(r.reason),
        busy: this.busy.has(r.id),
      };
      if (author?.avatarUrl !== undefined) row.authorAvatarUrl = author.avatarUrl;
      return row;
    });
  }
}
